import { guardNull } from "../misc/guard-null";
import { guardNumber } from "../numbers";
import type { CalculationFormServiceItem } from "../schemas";
import { generateId } from "../strings";
import type { NullableFields, ServerSet, ServiceUnit, Tables } from "../types";
import { calculateServiceCalculationItemTotal } from "./calculate-service-calculation-item-total";

/**
 * Prepares a calculation form item for persistence, computing its stored total
 * (a plain unit-based price for services, a rolled-up children-times-quantity price for blocks).
 *
 * @param item - The form item to prepare.
 * @param serviceCalculationId - The calculation this item belongs to.
 * @param allItems - The full flat list of items on the calculation, needed to resolve a block's children total.
 * @returns The row shape ready to upsert into `serviceCalculationItems`.
 */
export function prepareCalculationItem(
	item: CalculationFormServiceItem,
	serviceCalculationId: string,
	allItems: NullableFields<CalculationFormServiceItem>[],
): ServerSet<Tables<"serviceCalculationItems">> {
	const dbItem: ServerSet<
		Tables<"serviceCalculationItems">,
		"createdAt" | "createdBy" | "updatedAt" | "updatedBy"
	> = {
		type: item.type ?? "service",
		id: guardNull(item.id) || generateId(),
		shortText: item.shortText,
		longText: guardNull(item.longText),
		unitPrice: guardNumber(item.unitPrice),
		parentServiceCalculationItemId: guardNull(item.parentServiceCalculationItemId),
		ranking: guardNumber(item.ranking),
		isEventualPosition: item.isEventualPosition ?? false,
		...guardServiceUnitItem(item),
		organizationServiceId: guardNull(item.organizationServiceId),
		serviceCalculationId,
		total: item.isEventualPosition ? 0 : calculateServiceCalculationItemTotal(item, allItems),
	};

	return dbItem;
}

/**
 * Derives the `unit`/`quantity` columns for a calculation item. A block has no unit and
 * uses `quantity` as its children rollup multiplier; a service always holds its
 * measurements in its named positions instead, so its own `quantity` is unused.
 *
 * @param item - The item to derive the columns for.
 * @returns The `unit` and `quantity` values to persist.
 */
export function guardServiceUnitItem<
	T extends Pick<CalculationFormServiceItem, "type" | "unit" | "quantity">,
>(item: T) {
	if (item.type === "block") {
		return {
			unit: null,
			quantity: guardNumber(item.quantity) || 1,
		};
	}
	return {
		unit: item.unit as ServiceUnit,
		quantity: null,
	};
}
